import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, DetachedRouteHandle } from '@angular/router';

/**
 * Opt-in "keep alive" for routed components — the SPA version of the browser's
 * back/forward cache. A route with `data: { keepAlive: true }` is not destroyed
 * when the user navigates away: its component (DOM, grid, state) is parked and
 * re-attached as-is when they come back, so there is nothing to reload or
 * re-render.
 *
 * Every other route gets Angular's default behavior unchanged (this extends
 * BaseRouteReuseStrategy and only diverges for keepAlive routes).
 *
 * Rules for a parked route:
 *  - Keyed by its resolved path, so e.g. /reviews/incidents and
 *    /reviews/incidents/job/42 are parked separately.
 *  - Re-attached when the incoming URL has no query params (breadcrumb,
 *    sidebar) or exactly the params it was left with (Back, browser Back).
 *    Different params (a shared link) mean a different view: the parked one is
 *    destroyed and a fresh component is created.
 *  - The component decides when it is no longer worth keeping and calls
 *    drop() — e.g. Incidents drops itself once the user leaves the incidents
 *    section. Until then it stays alive (its ngOnDestroy has not run), so its
 *    subscriptions keep working while detached.
 */
@Injectable()
export class KeepAliveRouteReuseStrategy extends BaseRouteReuseStrategy {
  private parked = new Map<string, { handle: DetachedRouteHandle; query: string }>();

  static keyFor(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(r => r.url.map(segment => segment.path).join('/'))
      .filter(Boolean)
      .join('/');
  }

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data?.['keepAlive'] === true;
  }

  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = KeepAliveRouteReuseStrategy.keyFor(route);
    if (!handle) {
      // Angular calls store(route, null) right after re-attaching a parked
      // route: the component is live again, so just forget it — never destroy.
      this.parked.delete(key);
      return;
    }
    const previous = this.parked.get(key);
    if (previous && previous.handle !== handle) {
      this.destroy(previous.handle);
    }
    this.parked.set(key, { handle, query: this.queryOf(route) });
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (route.data?.['keepAlive'] !== true) return false;
    const key = KeepAliveRouteReuseStrategy.keyFor(route);
    const entry = this.parked.get(key);
    if (!entry) return false;

    const query = this.queryOf(route);
    if (query === '{}' || query === entry.query) return true;

    // Asked for a different view than the parked one: start fresh.
    this.drop(key);
    return false;
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (route.data?.['keepAlive'] !== true) return null;
    return this.parked.get(KeepAliveRouteReuseStrategy.keyFor(route))?.handle ?? null;
  }

  /** Destroy a parked route's component (running its ngOnDestroy) and forget it. */
  drop(key: string): void {
    const entry = this.parked.get(key);
    if (!entry) return;
    this.parked.delete(key);
    this.destroy(entry.handle);
  }

  private queryOf(route: ActivatedRouteSnapshot): string {
    const params = route.queryParams || {};
    return JSON.stringify(Object.keys(params).sort().reduce((acc, k) => ({ ...acc, [k]: params[k] }), {}));
  }

  // DetachedRouteHandle is opaque, but it carries the ComponentRef; destroying
  // it is the standard way to release a parked route.
  private destroy(handle: DetachedRouteHandle): void {
    (handle as any)?.componentRef?.destroy();
  }
}
